// API service for communicating with WordPress backend
const API_BASE = window.gwBpGeoSearchAjax?.ajaxurl || '/wp-admin/admin-ajax.php';
const NONCE = window.gwBpGeoSearchAjax?.nonce || '';
const REST_NONCE = window.gwBpGeoSearchAjax?.restNonce || '';
class ApiError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
        this.name = 'ApiError';
    }
}

async function makeRequest(action, data = {}) {
    const formData = new FormData();
    formData.append('action', action);
    formData.append('nonce', NONCE);
    
    console.log('API: makeRequest called with action:', action);
    console.log('API: makeRequest data:', data);
    
    Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
            if (key === 'xprofile_filters' && typeof data[key] === 'object') {
                // Send each filter as a separate field: xprofile_filters[fieldId]=value
                Object.keys(data[key]).forEach(fieldId => {
                    const value = data[key][fieldId];
                    if (Array.isArray(value)) {
                        // For arrays, always send as array format, even for single values
                        if (value.length === 0) {
                            // Don't send empty arrays
                            return;
                        } else if (value.length === 1) {
                            // For single-item arrays, still use array notation to ensure PHP receives it as array
                            formData.append(`xprofile_filters[${fieldId}][]`, value[0]);
                        } else {
                            // For multi-item arrays, send each value
                            value.forEach(item => {
                                formData.append(`xprofile_filters[${fieldId}][]`, item);
                            });
                        }
                    } else {
                        formData.append(`xprofile_filters[${fieldId}]`, value);
                    }
                });
            } else if (typeof data[key] === 'object') {
                formData.append(key, JSON.stringify(data[key]));
            } else {
                formData.append(key, data[key]);
            }
        }
    });
    
    // Debug: Log FormData contents
    console.log('API: FormData entries:');
    for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
    }

    try {
        const response = await fetch(API_BASE, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        });

        if (!response.ok) {
            throw new ApiError(`HTTP error! status: ${response.status}`, response.status);
        }

        const result = await response.json();
        
        if (!result.success) {
            throw new ApiError(result.data?.message || 'API request failed');
        }

        return result.data;
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(`Network error: ${error.message}`);
    }
}

export async function fetchMembers({ formId, search = '', filters = {}, bounds = null, includeNoLocation = false, limit = null, offset = 0 }) {
    console.log('API: fetchMembers called with filters:', filters);
    console.log('API: Filters object type:', typeof filters);
    console.log('API: Filters keys:', Object.keys(filters));
    
    // Filter out empty values before sending
    const cleanFilters = {};
    Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value && (
            (typeof value === 'string' && value.trim()) ||
            (Array.isArray(value) && value.length > 0) ||
            (typeof value === 'object' && value !== null && (value.min || value.max))
        )) {
            cleanFilters[key] = value;
        }
    });
    
    console.log('API: Clean filters being sent:', cleanFilters);
    
    return makeRequest('gw_bp_get_members', {
        form_id: formId,
        search_term: search,
        xprofile_filters: cleanFilters,
        bounds: bounds,
        include_no_location: includeNoLocation,
        limit: limit,
        offset: offset
    });
}

export async function getUserLocation() {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });
            },
            (error) => {
                reject(new Error(`Geolocation error: ${error.message}`));
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes
            }
        );
    });
}

// Messaging API functions
export async function sendMessage({ recipientId, subject, message }) {
    return makeRequest('gw_bp_send_message', {
        recipient_id: recipientId,
        subject: subject,
        message: message
    });
}

export async function getMessageThreads({ limit = 20, offset = 0 }) {
    return makeRequest('gw_bp_get_message_threads', {
        limit: limit,
        offset: offset
    });
}

// Favorites API functions - using GW BP Favorites REST API
const FAVORITES_API_BASE = '/wp-json/bp-favorites/v1';

async function makeFavoritesRequest(endpoint, method = 'GET', data = {}) {
    const url = `${FAVORITES_API_BASE}${endpoint}`;
    const options = {
        method: method,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': REST_NONCE
        }
    };

    if (method !== 'GET' && Object.keys(data).length > 0) {
        options.body = JSON.stringify(data);
    }

    try {
        const response = await fetch(url, options);
        
        if (!response.ok) {
            throw new ApiError(`Favorites API error! status: ${response.status}`, response.status);
        }

        return await response.json();
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(`Favorites network error: ${error.message}`);
    }
}

export async function getUserFavorites(userId) {
    return makeFavoritesRequest(`/${userId}`);
}

export async function addFavorite(favoriteUserId) {
    return makeFavoritesRequest('/add', 'POST', {
        favorite_id: favoriteUserId
    });
}

export async function removeFavorite(favoriteUserId) {
    return makeFavoritesRequest('/remove', 'POST', {
        favorite_id: favoriteUserId
    });
}

// Get all favorite user IDs for a user (returns array of user IDs)
export async function getAllUserFavorites(userId) {
    try {
        console.log('getAllUserFavorites called with userId:', userId);
        const favorites = await getUserFavorites(userId);
        console.log('Raw favorites response:', favorites);
        
        // Handle different possible response formats
        if (!favorites) {
            console.log('No favorites returned, returning empty array');
            return [];
        }
        
        if (Array.isArray(favorites)) {
            // If it's already an array, extract user IDs
            const userIds = favorites.map(fav => {
                // Try different possible property names
                return fav.user_id || fav.id || fav.favorite_id || fav;
            }).filter(id => id); // Filter out any null/undefined values
            
            console.log('Extracted user IDs:', userIds);
            return userIds;
        }
        
        // If it's an object, might be wrapped in a data property
        if (favorites.data && Array.isArray(favorites.data)) {
            const userIds = favorites.data.map(fav => fav.user_id || fav.id || fav.favorite_id || fav).filter(id => id);
            console.log('Extracted user IDs from data property:', userIds);
            return userIds;
        }
        
        console.log('Unexpected favorites format, returning empty array');
        return [];
    } catch (error) {
        console.error('Error in getAllUserFavorites:', error);
        // Return empty array on error rather than throwing
        return [];
    }
}
